import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { datasetsAPI } from '../../services/api';
import { readSheet, mapHeaders, buildRows, translateRowError } from '../../utils/datasetUpload';
import { COLUMN_TYPE_LABEL, formatNumber } from './dataSourceFormat';

const BATCH = 1000;
const describeError = (err, fallback) => err?.response?.data?.error || err?.message || fallback;

const EXAMPLE_VALUES = { string: 'text', number: 123.45, date: '2026-10-01', boolean: true };

/** The request body a sync tool sends to fill this dataset. */
export function exampleBody(schema) {
  return {
    schema: schema.columns.map((c) => ({ name: c.name, type: c.type })),
    rows: [Object.fromEntries(schema.columns.map((c) => [c.name, EXAMPLE_VALUES[c.type]]))],
    mode: schema.primaryKey ? 'upsert' : 'replace',
  };
}

/**
 * Ways to fill a dataset the company defines: upload a CSV or Excel file
 * (checked against the definition before anything changes), or push rows
 * through the API from a sync tool.
 */
const AddDataPanel = ({ schema, companyId, onUploaded, initialFile = null }) => {
  const [sheet, setSheet] = useState(null);
  const [fileName, setFileName] = useState('');
  const [mapping, setMapping] = useState(null);
  const [mode, setMode] = useState(schema.primaryKey ? 'upsert' : 'replace');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState('');

  const choose = async (file) => {
    setError('');
    setResult('');
    setSheet(null);
    setMapping(null);
    if (!file) return;
    try {
      setFileName(file.name);
      const parsed = await readSheet(await file.arrayBuffer());
      if (parsed.headers.length === 0) throw new Error('The file has no header row.');
      setSheet(parsed);
      setMapping(mapHeaders(parsed.headers, schema.columns));
    } catch (err) {
      setError(describeError(err, 'Could not read the file'));
    }
  };

  // The spreadsheet the dataset was created from, ready to upload.
  const offered = useRef(false);
  useEffect(() => {
    if (initialFile && !offered.current) {
      offered.current = true;
      choose(initialFile);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- once, for the file passed in
  }, [initialFile]);

  const upload = async () => {
    const rows = buildRows(sheet, mapping);
    const columns = mapping.matched.map((m) => ({ name: m.column.name, type: m.column.type }));
    let loadId = null;
    try {
      setBusy(true);
      setError('');
      const begun = await datasetsAPI.beginLoad(schema.name, companyId, { schema: columns, mode, ...(schema.primaryKey ? { primaryKey: schema.primaryKey } : {}) });
      loadId = begun.data.loadId;
      for (let i = 0; i < rows.length; i += BATCH) {
        await datasetsAPI.appendRows(schema.name, companyId, loadId, rows.slice(i, i + BATCH));
      }
      const committed = await datasetsAPI.commitLoad(schema.name, companyId, loadId);
      setResult(`Uploaded ${formatNumber(rows.length)} rows from ${fileName}. ${schema.name} now has ${formatNumber(committed.data.rowCount)} rows.`);
      setSheet(null);
      setMapping(null);
      onUploaded?.();
    } catch (err) {
      if (loadId) await datasetsAPI.abortLoad(schema.name, companyId, loadId).catch(() => {});
      setError(`Nothing was changed. ${translateRowError(describeError(err, 'The upload failed'), sheet)}`);
    } finally {
      setBusy(false);
    }
  };

  const preview = sheet && mapping ? buildRows({ rows: sheet.rows.slice(0, 5) }, mapping) : [];
  const apiUrl = `${window.location.origin}/api/datasets/${schema.name}`;

  return (
    <section className="source-card" aria-labelledby="add-data-heading">
      <h3 id="add-data-heading">Add data</h3>

      <h4>Upload a spreadsheet</h4>
      <p className="source-hint" style={{ marginTop: 0 }}>
        A CSV or Excel file whose first row holds the column names. Columns are matched by name, ignoring case and spaces.
      </p>
      <input type="file" accept=".csv,.xlsx,.xls" aria-label="Spreadsheet to upload" onChange={(e) => choose(e.target.files?.[0])} disabled={busy} />

      {error && <div className="error-message" role="alert">{error}</div>}
      {result && <div className="success-message" role="status">{result}</div>}

      {sheet && mapping && (
        <div className="upload-check">
          <p>
            <strong>{fileName}</strong>: {formatNumber(sheet.rows.length)} rows. Matched {mapping.matched.length} of {schema.columns.length} columns.
          </p>
          {mapping.missingRequired.length > 0 && (
            <p className="upload-problem" role="alert">
              Required column{mapping.missingRequired.length === 1 ? '' : 's'} missing from the file: {mapping.missingRequired.map((c) => c.name).join(', ')}.
            </p>
          )}
          {mapping.missingOptional.length > 0 && <p className="source-hint">Not in the file, left empty: {mapping.missingOptional.map((c) => c.name).join(', ')}.</p>}
          {mapping.ignored.length > 0 && <p className="source-hint">Not columns of this dataset, ignored: {mapping.ignored.join(', ')}.</p>}

          {preview.length > 0 && mapping.matched.length > 0 && (
            <div className="dataset-sample-wrapper">
              <table className="dataset-sample-table" aria-label="Upload preview">
                <thead><tr>{mapping.matched.map((m) => <th key={m.column.name}>{m.column.name} <small>{COLUMN_TYPE_LABEL[m.column.type]}</small></th>)}</tr></thead>
                <tbody>
                  {preview.map((row, i) => (
                    <tr key={i}>{mapping.matched.map((m) => <td key={m.column.name}>{row[m.column.name] === null ? '' : String(row[m.column.name])}</td>)}</tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="upload-actions">
            <label>
              <span>Rows</span>
              <select value={mode} onChange={(e) => setMode(e.target.value)} disabled={busy}>
                <option value="replace">Replace all rows with these</option>
                <option value="append">Add these rows</option>
                {schema.primaryKey && <option value="upsert">Add new rows and update existing ones by {schema.primaryKey}</option>}
              </select>
            </label>
            <button
              type="button"
              className="btn-primary"
              onClick={upload}
              disabled={busy || mapping.missingRequired.length > 0 || mapping.matched.length === 0 || sheet.rows.length === 0}
            >
              {busy ? 'Uploading…' : `Upload ${formatNumber(sheet.rows.length)} rows`}
            </button>
          </div>
        </div>
      )}

      <h4>Push from a sync tool</h4>
      <p className="source-hint" style={{ marginTop: 0 }}>
        Send rows to <code>POST {apiUrl}</code> with an <code>x-api-key</code> header. Create a key with the datasets scope under{' '}
        <Link to="/admin/api-keys">API keys</Link>. Pushes must match this definition; a push that does not is refused with the reason.
      </p>
      <details className="api-example">
        <summary>Example request body</summary>
        <pre>{JSON.stringify(exampleBody(schema), null, 2)}</pre>
      </details>
    </section>
  );
};

export default AddDataPanel;
